import React from 'react';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { useForm, FieldValues } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import {
  UPDATE_ENTITY_TERMINATION_OVERVIEW_TREASURY,
  GET_ENTITY_TERMINATION_OVERVIEW,
} from '../../../utils/statApi/EntityTerminationApi';
import EntityTerminationTreasuryEditForm, {
  ITreasuryInformation,
} from './EntityTerminationTreasuryEditForm';

interface IEntityTerminationTreasuryEditPanelProps {
  closePanel: () => void;
  companyCode: string;
  treasuryInfo?: ITreasuryInformation;
}

const EntityTerminationTreasuryEditPanel = (
  props: IEntityTerminationTreasuryEditPanelProps,
): JSX.Element => {
  const { closePanel, companyCode, treasuryInfo } = props;
  const treasuryForm = useForm<FieldValues>({
    defaultValues: {
      ...treasuryInfo,
    },
  });
  const { handleSubmit } = treasuryForm;

  const [updateTreasury, { loading, error }] = useMutation(
    UPDATE_ENTITY_TERMINATION_OVERVIEW_TREASURY,
    {
      onCompleted: closePanel,
      refetchQueries: [
        {
          query: GET_ENTITY_TERMINATION_OVERVIEW,
          variables: { companyCode },
        },
      ],
    },
  );

  const handleSaveClick = (formData: ITreasuryInformation): void => {
    updateTreasury({
      variables: {
        companyCode,
        updateOverviewTreasuryDto: {
          bankAccount: formData.bankAccount ?? null,
          ihcc: formData.ihcc ?? null,
          uscc: formData.uscc ?? null,
          loan: formData.loan ?? null,
        },
      },
    });
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Edit Treasury"
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      closeButtonAriaLabel="Close Treasury Edit Panel"
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          mutationLoading={loading}
          closePanel={closePanel}
          saveLabel="Save Treasury"
          saveTitle="Save Treasury"
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSaveClick)}
        />
      )}
    >
      <form>
        {error && <LoadingErrorMessage error={error} loading={loading} />}
        <EntityTerminationTreasuryEditForm form={treasuryForm} treasuryInfo={treasuryInfo} />
      </form>
    </CoherencePanel>
  );
};

export default EntityTerminationTreasuryEditPanel;
